// client/src/hooks/useAnnouncements.ts
//
// Muestra anuncios nuevos desde la última vez que el usuario cerró el modal.
// El timestamp se persiste en profiles.announcements_last_seen_at (Supabase).
//
// Lógica de "cuándo mostrar":
//   - Usuario nuevo (columna null): anuncios de los últimos 30 días.
//   - Usuario que volvió: anuncios publicados después de su última visita.
//   - Sin cambios nuevos: el modal no aparece.

import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

const FALLBACK_DAYS = 30;

export interface AnnouncementItem {
  en: string;
  es: string;
  type: 'fix' | 'new' | 'improvement';
}

export interface Announcement {
  id: string;
  title_en: string;
  title_es: string;
  items: AnnouncementItem[];
  created_at: string;
}

interface UseAnnouncementsResult {
  announcements: Announcement[];
  loading: boolean;
  dismiss: () => void;
}

export function useAnnouncements(user: User | null): UseAnnouncementsResult {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchAnnouncements() {
      try {
        // Leer el timestamp de la última vez que el usuario vio el modal
        let lastSeenAt: string | null = null;

        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('announcements_last_seen_at')
            .eq('id', user.id)
            .maybeSingle();
          lastSeenAt = profile?.announcements_last_seen_at ?? null;
        }

        // Sin timestamp (usuario nuevo): mostrar solo los últimos N días
        const since = lastSeenAt
          ?? new Date(Date.now() - FALLBACK_DAYS * 24 * 60 * 60 * 1000).toISOString();

        const { data, error } = await supabase
          .from('announcements')
          .select('id, title_en, title_es, items, created_at')
          .eq('active', true)
          .gt('created_at', since)
          .order('created_at', { ascending: false });

        if (cancelled) return;
        if (error || !data) return;

        setAnnouncements(data as Announcement[]);
      } catch {
        // silencioso — no romper la app si Supabase está pausado
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAnnouncements();
    return () => { cancelled = true; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function dismiss() {
    setAnnouncements([]);

    if (!user) return;

    // Persistir el timestamp en profiles — próxima carga solo mostrará lo nuevo desde ahora
    await supabase
      .from('profiles')
      .update({ announcements_last_seen_at: new Date().toISOString() })
      .eq('id', user.id);
  }

  return { announcements, loading, dismiss };
}
